/**
 * Builds meshes and static colliders from a `BuiltLevel` — the bridge between
 * `src/game/profile/` (pure data) and the renderer plus physics. The profile knows
 * nothing about three.js or Rapier; everything that does lives here.
 *
 * All architecture shares one fixed `RigidBody` with many colliders rather than one
 * body each — static bodies are cheap but not free, and a big house has many walls.
 *
 * Shadow policy: walls and ceilings **do** cast, and that is a reversal.
 *
 * It used to be the opposite, for a good reason at the time — the only light was the sun
 * outside, so letting architecture cast would have put every interior in darkness. That
 * reasoning died with the room lamps. A light hanging *inside* a room that is not
 * occluded by that room's own walls simply shines through them into the next room, and
 * the house glows like a paper lantern.
 *
 * With architecture casting, occlusion falls out for free and correctly: a doorway is a
 * real hole in the wall, so light crosses it; a closed door leaf fills that hole and
 * casts, so it does not; an open leaf swings aside and light passes. Windows are holes
 * too, which is how the storm gets in.
 *
 * Floors still do not cast — the house is single-storey, so there is nothing beneath
 * them to shade.
 *
 * An outdoor level arrives here as the same thing: its grass is a floor like any other and
 * its hedges and bank barriers are already in `collidableBoxes`, so only the *drawing* of
 * the maze is special-cased, in `MazeGrounds` below.
 */

import { CuboidCollider, RigidBody } from '@react-three/rapier';
import type { Material } from 'three';
import {
  collidableBoxes,
  GROUNDS_ROOM_ID,
  type BuiltLevel,
  type RoomWall,
} from '../game/profile/buildGeometry';
import { buildFixtures } from '../game/profile/buildFixtures';
import { buildLighting } from '../game/profile/buildLighting';
import { buildTrim } from '../game/profile/buildTrim';
import type { Box, LevelSpec } from '../game/profile/types';
import { useGameStore } from '../store/gameStore';
import { boxPosition, halfExtents } from './boxUnits';
import { groundsMaterial, surfaceMaterial, toonMaterial } from './materials';
import { MazeGrounds } from './MazeGrounds';
import { RoomFixtures } from './RoomFixtures';
import { RoomTrim } from './RoomTrim';
import { WindowGlass } from './WindowGlass';

type Props = {
  built: BuiltLevel;
  level: LevelSpec;
};

/** World metres covered by one tile of each procedural pattern. */
const FLOOR_TILE = 2;
const WALL_TILE = 2.4;
/** Reference wall height the pattern was drawn for, used to scale trimless offcuts. */
const WALL_HEIGHT_TILE = 2.6;

function BoxMesh({ box, material, castShadow = false }: { box: Box; material: Material; castShadow?: boolean }) {
  return (
    <mesh position={boxPosition(box)} receiveShadow castShadow={castShadow} material={material}>
      <boxGeometry args={[box.size.x, box.size.y, box.size.z]} />
    </mesh>
  );
}

/**
 * The pattern repeats along the wall's long horizontal axis and in proportion to its
 * height, so wallpaper stripes are the same width and the same scale on every wall
 * however long it runs or how tall the room is. Skirting and cornice are no longer part
 * of the texture — they are geometry (see `RoomTrim`), which is why nothing here needs
 * to special-case a full-height wall any more.
 */
function wallMaterial(wall: RoomWall) {
  const run = Math.max(wall.box.size.x, wall.box.size.z);
  const repeatY = Math.max(0.25, wall.box.size.y / WALL_HEIGHT_TILE);
  return surfaceMaterial(wall.pattern, wall.color, run / WALL_TILE, repeatY);
}

export function LevelGeometry({ built, level }: Props) {
  const roomLights = useGameStore((store) => store.roomLights);
  const solids = collidableBoxes(built);
  // No useMemo: the React Compiler memoizes this, and doing it by hand defeats it.
  const trim = buildTrim(built);
  const fixtures = buildFixtures(built, buildLighting(built, level));

  return (
    <>
      {built.floors.map(({ roomId, box, color, pattern }) => (
        <BoxMesh
          key={`floor-${roomId}`}
          box={box}
          /* The grass is the one floor that is not a repeating tile: its image carries
             the mud path along the maze's actual corridors, so it maps over the whole
             lawn exactly once. Every room floor tiles as before. */
          material={
            built.grounds && roomId === GROUNDS_ROOM_ID
              ? groundsMaterial(
                  color,
                  {
                    x: box.center.x - box.size.x / 2,
                    z: box.center.z - box.size.z / 2,
                    width: box.size.x,
                    depth: box.size.z,
                  },
                  built.grounds.paths,
                )
              : surfaceMaterial(pattern, color, box.size.x / FLOOR_TILE, box.size.z / FLOOR_TILE)
          }
        />
      ))}
      {built.ceilings.map(({ roomId, box, color }) => (
        <BoxMesh key={`ceiling-${roomId}`} box={box} material={toonMaterial(color)} castShadow />
      ))}
      {built.walls.map((wall, index) => (
        <BoxMesh key={`wall-${index}`} box={wall.box} material={wallMaterial(wall)} castShadow />
      ))}
      {built.steps.map((step, index) => (
        <BoxMesh key={`step-${index}`} box={step} material={toonMaterial(built.wallColor)} castShadow />
      ))}

      <WindowGlass faces={built.windowFaces} />
      <RoomTrim pieces={trim} />
      <RoomFixtures fixtures={fixtures} roomLights={roomLights} trimColor={built.trimColor} />
      {/* Hedges and moat, for a level that has grounds instead of rooms. Conditional so
          the house's render path is exactly what it was: a level with no `grounds` never
          reaches a line of this. The colliders are not conditional and do not need to be —
          `collidableBoxes` already returns hedges and bank barriers alongside the walls. */}
      {built.grounds && <MazeGrounds grounds={built.grounds} />}

      <RigidBody type="fixed" colliders={false}>
        {solids.map((box, index) => (
          <CuboidCollider key={`collider-${index}`} args={halfExtents(box)} position={boxPosition(box)} />
        ))}
      </RigidBody>
    </>
  );
}
